import React, { useState, useEffect } from "react";
import { Box, Typography, Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../services/api-service";
import NoData from "../../components/Nodata";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";

export default function SimpleVideoCarousel({
  studioId = "67328ffd0f1eb21571707e5f",
  isOwner,
}) {
  const { t } = useTranslation("common");
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null); // For popup
  const [isModalOpen, setModalOpen] = useState(false);

  // Fetch videos data
  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await ApiService.VIDEO_USER(studioId);
      if (response.code === 200 && response.result && response.result.length > 0) {
        setVideos(response.result);
      } else {
        setVideos([]);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching videos:", error);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [studioId]);

  const handleVideoClick = (video) => {
    setSelectedVideo(video.video.url); // Use the correct video URL
    setModalOpen(true); // Open the modal
  };

  const closeModal = () => {
    console.log("Close button clicked"); // Debugging
    setSelectedVideo(null);
    setModalOpen(false);
  };
  

  return (
    <Box
      sx={{
        width: "100%",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        borderRadius: "16px",
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "#FFFFFF",
          fontWeight: "bold",
          fontFamily: "Inter, sans-serif",
          alignSelf: "flex-start",
          marginBottom: "20px",
        }}
      >
        {t("carousel.title", { defaultValue: "Featured Videos" })}
      </Typography>

      <Box
  sx={{
    display: "flex",
    gap: "20px",
    overflowX: "scroll", // Ensure horizontal scrolling
    scrollSnapType: "x mandatory", // Enable smooth snapping
    borderRadius: "12px",
    padding: "10px",
    width: "100%",
    "&::-webkit-scrollbar": {
      display: "none", // Hide scrollbar
    },
  }}
  >
        {loading ? (
          <Loader />
        ) : videos.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: "30px",
              width: "100%",
            }}
          >
            {videos.map((video) => (
              <Box
                key={video._id}
                sx={{
                  cursor: "pointer",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    borderRadius: "12px",

                    boxShadow: "0 8px 20px rgba(255, 255, 255, 0.3)",
                  },
                }}
                onClick={() => handleVideoClick(video)} // Open popup on click
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "250px",
                    height: "auto", // Let height adjust based on aspect ratio
                    borderRadius: "12px",
                    overflow: "hidden",
                    backgroundColor: "#333",
                  }}
                >
                  <img
                    src={video.thumbnail.url}
                    alt={video.title}
                    style={{
                      position: "relative",
                      top: 0,
                      left: 0,
                      borderRadius: "12px",
                      width: "100%",
                      height: "90%",
                      objectFit: "fill",
                      aspectRatio: "16 / 9", // Lock the aspect ratio
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <NoData />
        )}
      </Box>

      {/* Video Popup Modal */}
      <Modal
        open={isModalOpen}
        onClose={closeModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",

        }}
      >
          <Box
            sx={{
              position: "relative",
              width: "80%",
              maxWidth: "800px",
              backgroundColor: "#000",
              borderRadius: "16px",
              padding: "20px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
              zIndex: 1400, // Ensure container is above the backdrop
            }}
          >
            <IconButton
              onClick={closeModal}
              sx={{
                position: "absolute",
                top: "10px",
                right: "10px",
                color: "#fff",
                width: "48px",
                height: "48px",
                zIndex: 1500, // Ensure button is on top of everything
              }}
            >
              <CloseIcon sx={{ fontSize: "24px" }} />
            </IconButton>

            <video
              controls
              autoPlay
              style={{
                width: "100%",
                height: "auto", // Maintain aspect ratio
                borderRadius: "12px",
                zIndex: 1000, // Video below the button
              }}
            >
              <source src={selectedVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Box>

      </Modal>
    </Box>
  );
}
